import Box from '@risk-smart/themed-cloudscape-components/box';
import Button from '@risksmart-app/components/src/button';
import ErrorContent from '@risksmart-app/components/src/error-pages/ErrorContent';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from 'src/components/loading';

import { useGetRoleAccess } from '@/hooks/queries/role-access/useGetRoleAccess';

import { PermissionsContext } from './PermissionsContext';
import { useTypePermissions } from './useTypePermissions';

type Props = { children: ReactNode };

export const PermissionsProvider: FC<Props> = ({ children }) => {
  // Under V3 the bridge skips the getRoleAccess read and returns an empty
  // (non-null) catalog — permission decisions come from the perm_v2 batch
  // endpoints, not role_access. The legacy (trpc-off) path fetches as before.
  const { roleAccess, loading, error } = useGetRoleAccess();
  const { t } = useTranslation('common', {
    keyPrefix: 'permissionsUnavailable',
  });

  // ...which also means `error` above is hardcoded false under V3, leaving this
  // gate permanently open on the very path that now resolves permissions. The
  // V3 source of truth is `getTypePermissions`, and when it fails every
  // consumer reads a false from `hasTypePermission` — indistinguishable from a
  // real denial. Without this the app renders a definite "Access Denied" for a
  // read that never answered (COI-1062).
  const { isError: typePermissionsError, refetch: retryTypePermissions } =
    useTypePermissions();

  const permissionsUnavailable = error || typePermissionsError;

  return (
    <PermissionsContext.Provider value={roleAccess ?? null}>
      {loading && <Loading data-loading-reason={'permissions'} />}
      {permissionsUnavailable && (
        <ErrorContent
          title={'Sorry, we couldn’t make that happen'}
          imgSrc={'/errors/rubiks-cube.png'}
          imgAlt={'binoculars'}
          // Home sits behind this same provider, so "Back to RiskSmart" cannot
          // clear a type-permissions failure — it re-renders straight back into
          // this branch. Offer the retry that can instead.
          hideBackToHome={typePermissionsError}
        >
          <Box variant={'p'}>
            {typePermissionsError ? t('description') : 'Please try again later'}
          </Box>
          {typePermissionsError && (
            <Box margin={{ top: 'l' }}>
              {/* Deliberately user-initiated. `shouldRetryTrpcQuery` already
                  spends three automatic attempts at this hop, and the V3
                  resilience policy is one retry layer per hop — a fourth
                  automatic attempt would amplify against a service that is by
                  definition already struggling. */}
              <Button
                variant={'primary'}
                onClick={() => {
                  void retryTypePermissions();
                }}
              >
                {t('retry')}
              </Button>
            </Box>
          )}
        </ErrorContent>
      )}
      {!permissionsUnavailable && !loading && children}
    </PermissionsContext.Provider>
  );
};
